import React, { useEffect, useRef } from 'react';

// Shared search box for the table pages. Pressing "/" anywhere on the page
// (when not already typing) focuses the input; Esc clears and blurs it.
// The kbd hint is shown only while the field is empty and unfocused.

interface SearchInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  width?: number;
}

export function SearchInput({ value, onChange, placeholder = 'Search…', width = 240 }: SearchInputProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      if (e.key === '/' && !typing) {
        e.preventDefault();
        ref.current?.focus();
      } else if (e.key === 'Escape' && document.activeElement === ref.current) {
        onChange('');
        ref.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onChange]);

  return (
    <div className="relative">
      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" width="12" height="12" viewBox="0 0 13 13" fill="none">
        <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
        <path d="M9.5 9.5l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-7 pr-7 py-1 rounded-lg border border-trim text-xs outline-none focus:border-teal-400 bg-white"
        style={{ width }}
      />
      {!value && (
        <kbd className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-medium text-muted bg-wash border border-trim rounded px-1 py-0.5 leading-none pointer-events-none">/</kbd>
      )}
    </div>
  );
}
